// The stored format of a system row's content. Every loader version on the domain reads the same
// rows, so a change here must stay readable by older loaders.
// See .claude/context/messages.md ("System row format").

/**
 * The content fields of a system row, in the format this loader stores.
 * `content` is a string, because loaders before 5.7.0 render it as a React child.
 * `systemContent` holds every field.
 * @param {{ text: string, error?: string, errorTitle?: string }} fields
 * @returns {{ content: string, systemContent: Object }}
 */
export const systemRowContent = (fields) => ({ content: fields.text, systemContent: fields });

/**
 * A system row's content as `{ text, error, errorTitle }`, from any stored format.
 * @param {Object} row
 * @returns {{ text?: string, error?: string, errorTitle?: string }}
 */
export const readSystemContent = ({ content, systemContent }) => {
  // An object `content` is always the newest write: 5.7.0–5.9.0 write no other format.
  if (content && typeof content === 'object') return content;
  // A loader before 5.7.0 can replace `content` and leave an older `systemContent`.
  if (systemContent && systemContent.text === content) return systemContent;
  return { text: content };
};

/**
 * Rewrites a system row into the format `systemRowContent` writes. Returns `row` itself when it
 * already has that format, so the startup rewrite writes only the rows that change.
 * @param {Object} row
 * @returns {Object}
 */
export const toCurrentSystemRow = (row) => {
  if (row?.type !== 'system') return row;
  const { systemContent, ...fields } = row;
  const next = { ...fields, ...systemRowContent(readSystemContent(row)) };
  const isUnchanged = typeof next.content === typeof row.content
    && next.systemContent === systemContent;
  return isUnchanged ? row : { ...next, updatedAt: Date.now() };
};
